import { loadingMessage } from '@nrx/ui';
import { lazy, Suspense, type JSX } from 'react';
import { Navigate, type RouteObject } from 'react-router';

import { PageFallback, ShellPageFallback } from './components/docs/page-fallback.js';
import { DocsLayout, DocsLayoutCatalog } from './components/docs-shell/docs-layout.js';

const GettingStartedPage = lazy(() => import('./pages/getting-started-page.js'));
const AllComponentsPage = lazy(() => import('./pages/all-components-page.js'));
const ComponentDocPage = lazy(() => import('./pages/component-doc-page.js'));
const ApiRedirectPage = lazy(() => import('./pages/api-redirect-page.js'));
const BrandPage = lazy(() => import('./pages/brand-page.js'));
const ColorsPage = lazy(() => import('./pages/colors-page.js'));
const ElevationPage = lazy(() => import('./pages/elevation-page.js'));
const SpacingPage = lazy(() => import('./pages/spacing-page.js'));

const COMPONENTS_LIST_MESSAGE = loadingMessage({ resource: 'component', operation: 'list' });
const COMPONENT_GET_MESSAGE = loadingMessage({ resource: 'component', operation: 'get' });

function withDocsLayout(page: JSX.Element, message?: string): JSX.Element {
  return (
    <DocsLayout>
      <Suspense
        fallback={message !== undefined ? <PageFallback message={message} /> : <PageFallback />}
      >
        {page}
      </Suspense>
    </DocsLayout>
  );
}

function withDocsCatalogLayout(page: JSX.Element, message?: string): JSX.Element {
  return (
    <DocsLayoutCatalog>
      <Suspense
        fallback={message !== undefined ? <PageFallback message={message} /> : <PageFallback />}
      >
        {page}
      </Suspense>
    </DocsLayoutCatalog>
  );
}

/** For pages that render their own DocsLayoutWide — the fallback supplies the shell. */
function withShellFallback(page: JSX.Element, message?: string): JSX.Element {
  return (
    <Suspense
      fallback={
        message !== undefined ? <ShellPageFallback message={message} /> : <ShellPageFallback />
      }
    >
      {page}
    </Suspense>
  );
}

/**
 * Route table shared by the app and routing tests.
 *
 * Kept out of `app.tsx` so tests can import it without also constructing the
 * production `createBrowserRouter` (which fights `createMemoryRouter` under jsdom).
 */
export const ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/getting-started" replace />,
  },
  {
    path: '/getting-started',
    element: withDocsLayout(<GettingStartedPage />),
  },
  {
    path: '/components',
    element: withDocsCatalogLayout(<AllComponentsPage />, COMPONENTS_LIST_MESSAGE),
  },
  {
    path: '/components/:slug',
    element: withShellFallback(<ComponentDocPage />, COMPONENT_GET_MESSAGE),
  },
  {
    path: '/foundations/brand',
    element: withShellFallback(<BrandPage />),
  },
  {
    path: '/foundations/colors',
    element: withShellFallback(<ColorsPage />),
  },
  {
    path: '/foundations/elevation',
    element: withShellFallback(<ElevationPage />),
  },
  {
    path: '/foundations/spacing',
    element: withShellFallback(<SpacingPage />),
  },
  {
    path: '/api/:slug',
    element: withShellFallback(<ApiRedirectPage />),
  },
];
